<script setup lang="ts">
import api from "@/api";
import { baseURL } from "@/api/config";
import { utcToLocalDateTime } from "@/assets/js/dateHelper";
import type { TDriver, TDriverStatus } from "@/assets/js/globalTypes";
import { debounce } from "@/assets/js/helpers";
import { $t } from "@/i18n";
import { useRoute, useRouter } from "vue-router";
import driverAltIcon from "@/assets/image/default-driver.png";
import TableRow from "./tableRow.vue";
import NewModernInput from "@/components/input/NewModernInput.vue";
definePage({
	name: "TaxiPage",
	meta: {
		requiresLogin: true,
	},
});

const pageLimit = 8;

const drivers = ref<TDriver[]>([]);
const count = ref<number>(0);
const search = ref("");

const route = useRoute();
const router = useRouter();

onBeforeRouteLeave((to) => {
	if (to.query.page !== undefined) {
		const { page, ...restQuery } = to.query;
		return { path: to.path, query: restQuery };
	}
});

const driverStatus = ref<{
	type: TDriverStatus;
	label: string;
}>({
	type: "all",
	label: $t("app.multiselect.all"),
});

const driverVerified = ref<{
	type: "all" | "verified" | "unverified";
	label: string;
}>({
	type: "all",
	label: $t("app.multiselect.all"),
});

const subscriptionStatus = ref<{
	type: "all" | "active" | "inactive";
	label: string;
}>({
	type: "all",
	label: $t("app.multiselect.all"),
});

const multiselectStatusOptions = [
	{
		type: "all",
		label: $t("app.multiselect.all"),
	},
	{
		type: "active",
		label: $t("app.driver.active"),
	},
	{
		type: "inride",
		label: $t("app.driver.in-ride"),
	},
	{
		type: "inactive",
		label: $t("app.driver.inactive"),
	},
];

const multiselectVerifiedOptions = [
	{
		type: "all",
		label: $t("app.multiselect.all"),
	},
	{
		type: "verified",
		label: $t("app.driver.verified"),
	},
	{
		type: "unverified",
		label: $t("app.driver.unverified"),
	},
];

const multiselectSubscriptionOptions = [
	{
		type: "all",
		label: $t("app.multiselect.all"),
	},
	{
		type: "active",
		label: $t("app.driver.subscription-active"),
	},
	{
		type: "inactive",
		label: $t("app.driver.subscription-inactive"),
	},
];

const handleInput = debounce(repopulateDrivers, 300, false);

const pageCount = computed(() => Math.ceil(count.value / pageLimit));

const currentPage = computed(() => {
	return Number(route.query.page) || 1;
});

onMounted(() => {
	getDrivers();
});

async function getDrivers(limit = pageLimit, offset = 0) {
	try {
		const res = await api.getDrivers({
			status: driverStatus.value.type,
			limit,
			offset,
			search: search.value.trim() || undefined,
			verified: driverVerified.value.type,
			subscriptionFilter: subscriptionStatus.value.type,
		});

		drivers.value = res.data.data.data;
		count.value = res.data.data.meta_info.count;
	} catch (error) {
		console.log(error);
	}
}

function handlePageChange(pageNumber: number) {
	const offset = pageLimit * (pageNumber - 1);

	getDrivers(pageLimit, offset);
}

function repopulateDrivers() {
	router.push({
		name: "TaxiPage",
	});

	getDrivers();
}

function openDriver(driverId: number) {
	router.push(`/taxi/${driverId}`);
}

function isLowRating(driver: TDriver) {
	return driver.tax_rating !== null && Number(driver.tax_rating) < 0.8;
}
watch(
	[
		() => driverStatus.value.type,
		() => driverVerified.value.type,
		() => subscriptionStatus.value.type,
	],
	() => {
		repopulateDrivers();
	},
);
</script>

<template>
	<div id="taxi-page">
		<div class="content">
			<div class="control-bar">
				<div class="filters">
					<div class="control-bar-item">
						<label>{{ $t("app.driver.status") }}:</label>

						<div class="multiselect-wrap">
							<new-multi-select
								v-model="driverStatus"
								:options="multiselectStatusOptions"
								:valueProp="'type'"
								:slot-text="'label'"
							/>
						</div>
					</div>

					<div class="control-bar-item">
						<label>{{ $t("app.driver.verification") }}:</label>

						<div class="multiselect-wrap">
							<new-multi-select
								v-model="driverVerified"
								:options="multiselectVerifiedOptions"
								:valueProp="'type'"
								:slot-text="'label'"
							/>
						</div>
					</div>

					<div class="control-bar-item">
						<label>{{ $t("app.driver.subscription") }}:</label>

						<div class="multiselect-wrap">
							<new-multi-select
								v-model="subscriptionStatus"
								:options="multiselectSubscriptionOptions"
								:valueProp="'type'"
								:slot-text="'label'"
							/>
						</div>
					</div>
				</div>

				<div class="search-wrap">
					<new-modern-input
						v-model="search"
						:label="''"
						:bind="{
							type: 'text',
							placeholder: $t('app.labels.search'),
						}"
						@input="handleInput"
					/>
				</div>
			</div>

			<div
				v-if="drivers.length > 0"
				class="table-wrapper"
			>
				<table class="drivers-table">
					<thead>
						<tr>
							<th>Vozač</th>

							<th>
								{{ $t("app.driver.car") }}
							</th>

							<th>
								{{ $t("app.driver.plates") }}
							</th>

							<th>
								{{ $t("app.driver.assotiation") }}
							</th>

							<th>
								{{ $t("app.driver.subscription") }}
							</th>

							<th>
								{{ $t("app.driver.subscription-end") }}
							</th>

							<th>
								{{ $t("app.driver.verification") }}
							</th>
						</tr>
					</thead>

					<tbody>
						<table-row
							v-for="driver in drivers"
							:key="driver.tax_id"
							@click="openDriver(driver.tax_id)"
						>
							<td>
								<div class="driver-info">
									<div
										class="avatar"
										:class="{ active: Number(driver.sub_is_active) === 1 }"
									>
										<img
											:src="
												driver.image ? baseURL + driver.image : driverAltIcon
											"
											:alt="
												`${driver.tax_name || ''} ${driver.tax_lastname || ''}`.trim()
											"
										/>
									</div>

									<span
										class="driver-name"
										:class="{ 'low-rating': isLowRating(driver) }"
									>
										{{ driver.tax_name }}
										{{ driver.tax_lastname }}
									</span>
								</div>
							</td>

							<td>
								{{ driver.tax_model || "-" }}
							</td>

							<td>
								{{ driver.tax_plates || "-" }}
							</td>

							<td>
								{{ driver.aso_name || "-" }}
							</td>

							<td>
								<span class="subscription-status">
									{{
										driver.sub_is_active
											? $t("app.driver.subscription-active")
											: $t("app.driver.subscription-inactive")
									}}
								</span>
							</td>

							<td>
								<span v-if="driver.sub_is_active && driver.sub_end_time">
									{{ utcToLocalDateTime(driver.sub_end_time) }}
								</span>

								<span v-else>-</span>
							</td>

							<td>
								<span class="verification-status">
									{{
										driver.tax_privilege
											? $t("app.driver.verified")
											: $t("app.driver.unverified")
									}}
								</span>
							</td>
						</table-row>
					</tbody>
				</table>
			</div>

			<div
				v-else
				class="no-result"
			>
				<h1>
					{{ $t("app.labels.noresult") }}
				</h1>
			</div>

			<div class="pagination-wrap">
				<pagination-comp
					:total-pages="pageCount"
					:current-page="currentPage"
					@page-changed="handlePageChange"
				/>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.content {
	width: 100%;
	padding: 30px;
	padding-left: 80px;
	padding-right: 80px;
	h2 {
		margin-bottom: 10px;
		font-size: 1.5rem;
		text-align: center;
	}

	.control-bar {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		padding-left: 15px;
		padding-right: 15px;
		gap: 24px;
		width: 100%;
		margin-bottom: 20px;

		.filters {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 24px;
		}

		.control-bar-item {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 8px;

			label {
				flex-shrink: 0;
				white-space: nowrap;
			}
		}

		.multiselect-wrap {
			width: 158px;
			flex: 0 0 158px;
		}

		.search-wrap {
			width: 337px;
			flex: 0 0 337px;
		}
	}
}

.table-wrapper {
	width: 100%;
	overflow-x: auto;
	border: 1px solid rgba($app-accent-color1, 0.2);
	border-radius: 8px;
}

.drivers-table {
	width: 100%;
	min-width: 1150px;
	border-collapse: collapse;
	font-family: Inter, sans-serif;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 150%;
	letter-spacing: 0;

	thead {
		background-color: rgba($app-accent-color1, 0.08);
	}

	th {
		padding: 14px 16px;
		font-size: 16px;
		font-style: normal;
		font-weight: 600;
		line-height: 100%;
		letter-spacing: 0;
		text-align: left;
		white-space: nowrap;
	}
}

.driver-info {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 200px;
}

.avatar {
	position: relative;
	display: flex;
	flex: 0 0 50px;
	justify-content: center;
	align-items: center;
	box-sizing: border-box;
	width: 50px;
	height: 50px;
	overflow: hidden;
	border: 2px solid transparent;
	border-radius: 50%;

	&.active {
		border-color: $avatar-border-color;
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}
}

.subscription-status,
.verification-status {
	color: inherit;
	white-space: nowrap;
}

.driver-name {
	white-space: nowrap;

	&.low-rating {
		color: $error-color;
	}
}

.no-result {
	display: flex;
	justify-content: center;
	margin-top: 30px;
}

.pagination-wrap {
	position: fixed;
	bottom: 10px;
	left: 50%;
	display: flex;
	justify-content: center;
	width: fit-content;
	padding: 5px 10px;
	background: #ffffffd8;
	transform: translateX(-50%);
}

@media screen and (width <= 768px) {
	.content {
		padding: 15px;
	}

	.control-bar {
		flex-direction: column;
		align-items: stretch;
		gap: 15px;

		.filters {
			flex-direction: column;
			align-items: stretch;
			gap: 15px;
		}

		.control-bar-item {
			width: 100%;

			label {
				min-width: 100px;
			}
		}

		.multiselect-wrap {
			flex: 1;
			width: auto;
		}

		.search-wrap {
			flex: none;
			width: 100%;
		}
	}

	.pagination-wrap {
		position: static;
		width: fit-content;
		margin: 20px auto 0;
		transform: none;
	}
}
</style>
